<script setup lang="ts">
import type { ISiteIdentifier } from '@/components/rc/common/RcSiteSelect.vue'
import { useSiteMap } from '@/composables/eos/site-map'
import { dateTimeFormatter } from '@/helpers/ep/formatters'
import { formatHashrateToTerahashValue } from '@/util/formatters'
import { SiteType } from '@/types/core/Site'
import type { ISiteMapMinerFilters } from '@/types/eos/filters'
import type { IHostingClient, ISiteMapRack, ISiteMapStructure } from '@/types/eos/SiteMap'
import { DEFAULT_PAGE_SIZES, DEFAULT_PAGINATION_LAYOUT } from '@/types/shared/pagination'
import { Icon } from '@iconify/vue'
import { reactive, ref } from 'vue'
import EosSiteMapRackSelect from '../common/EosSiteMapRackSelect.vue'

const filters = reactive<ISiteMapMinerFilters>({
  page: 1,
  per_page: DEFAULT_PAGE_SIZES[2],
  site_id: undefined,
  structure_id: undefined,
  rack_id: undefined,
  hosting_client_id: undefined,
  name: undefined,
  ip: undefined,
})

const selectedTags = ref<string[]>([])

const { getMinersQuery } = useSiteMap()
const { data, isError, isPending, isRefetching, error, refetch } = getMinersQuery(filters)

const onSiteFilterUpdate = (value?: ISiteIdentifier) => {
  filters.site_id = value?.site_id ?? undefined
}

const onStructureFilterUpdate = (value?: ISiteMapStructure) => {
  filters.structure_id = value?.id ?? undefined
}

const onRackFilterUpdate = (value?: ISiteMapRack) => {
  filters.rack_id = value?.id ?? undefined
}

const onHostingClientFilterUpdate = (value?: IHostingClient) => {
  filters.hosting_client_id = value?.id ?? undefined
}

const onTagFilterUpdate = (value?: string[]) => {
  if (!value || value.length === 0) {
    filters.tags = undefined
    return
  }

  filters.tags = value.join(',')
}

const nameSearchSettings = {
  key: 'search',
  value: 'Search',
  type: 'search',
  placeholder: 'Name',
  style: 'width: 200px',
  onChange: (val: string) => {
    if (val == '') {
      filters.name = undefined
    } else {
      filters.name = val
    }
  },
}

const ipSearchSettings = {
  key: 'search',
  value: 'Search',
  type: 'search',
  placeholder: 'IP',
  style: 'width: 200px',
  onChange: (val: string) => {
    if (val == '') {
      filters.ip = undefined
    } else {
      filters.ip = val
    }
  },
}
</script>

<template>
  <rl-error-result v-if="isError && !isRefetching" :error="error">
    <el-button @click="refetch()">
      <Icon icon="material-symbols:refresh-rounded" height="16" width="16" :inline="true" />&nbsp;Reload
    </el-button>
  </rl-error-result>
  <template v-else>
    <div class="flex gap-2">
      <RcSiteSelect
        placeholder="Site ID or Name"
        :type="SiteType.DataCenter"
        :disabled="isPending"
        style="width: 200px"
        @update:model-value="onSiteFilterUpdate"
      />
      <EosSiteMapStructureSelect
        placeholder="Structure"
        :disabled="isPending"
        style="width: 200px"
        :site-id="filters.site_id"
        @update:model-value="onStructureFilterUpdate"
      />
      <EosSiteMapRackSelect
        placeholder="Rack"
        :disabled="isPending"
        style="width: 200px"
        :site-id="filters.site_id"
        :structure-id="filters.structure_id"
        @update:model-value="onRackFilterUpdate"
      />
      <EosHostingClientSelect
        placeholder="Hosting Client"
        :disabled="isPending"
        style="width: 200px"
        @update:model-value="onHostingClientFilterUpdate"
      />
      <TableSearchFilter :search-settings="nameSearchSettings" :disabled="isPending" />
      <TableSearchFilter :search-settings="ipSearchSettings" :disabled="isPending" />
      <el-select-v2
        v-model="selectedTags"
        placeholder="Tags"
        :disabled="isPending"
        clearable
        multiple
        filterable
        allow-create
        :show-arrow="false"
        collapse-tags
        no-data-text="Type the tags..."
        collapse-tags-tooltip
        :max-collapse-tags="0"
        style="width: 200px"
        :options="[]"
        @change="onTagFilterUpdate"
      />
    </div>
    <el-table
      v-loading="isPending"
      :data="data?.data"
      :stripe="true"
      :empty-text="isPending ? 'Loading...' : 'No data.'"
    >
      <el-table-column label="Name" prop="name" :min-width="192" />
      <el-table-column label="Hosting Client" prop="hosting_client.name" :min-width="192" />
      <el-table-column label="Site" prop="site.name" :min-width="192" />
      <el-table-column label="Structure" prop="structure.name" :min-width="120" />
      <el-table-column label="Rack" prop="rack.name" :min-width="120" />
      <el-table-column label="IP" prop="ip" :min-width="120" />
      <el-table-column label="Model" prop="model" :min-width="192" />
      <el-table-column label="Tags" prop="tags" :min-width="192">
        <template #default="{ row }">
          <div class="flex gap-2 flex-wrap">
            <el-tag v-for="tag in row.tags" :key="tag">{{ tag }}</el-tag>
          </div>
        </template>
      </el-table-column>
      <el-table-column label="Foreman ID" prop="foreman_id" :min-width="120" />
      <el-table-column prop="last_hashrate_value" label="Last Hashrate (TH/s)" :min-width="120">
        <template #default="scope">
          <el-tooltip
            v-if="scope.row.last_hashrate_timestamp"
            placement="top"
            :content="`Measured at ${dateTimeFormatter(scope.row, null, scope.row.last_hashrate_timestamp)}`"
          >
            <span class="cursor-default">{{ formatHashrateToTerahashValue(scope.row.last_hashrate_value) }}</span>
          </el-tooltip>
          <span v-else>{{ formatHashrateToTerahashValue(scope.row.last_hashrate_value) }}</span>
        </template>
      </el-table-column>
      <el-table-column label="Last Updated" prop="updated_at" :min-width="224" :formatter="dateTimeFormatter" />
    </el-table>
    <el-pagination
      v-if="data"
      v-model:current-page="filters.page"
      v-model:page-size="filters.per_page"
      class="flex justify-end"
      background
      :layout="DEFAULT_PAGINATION_LAYOUT"
      :page-sizes="DEFAULT_PAGE_SIZES"
      :page-count="data.total_pages"
      :total="data.total_records"
      hide-on-single-page
    />
  </template>
</template>
